import { get, round } from 'lodash'
import { ArrowDown, Frown, Info, PartyPopper } from 'lucide-react'
import {
  Column,
  Flex,
  ProgressBar,
  Row,
  Text,
  Tooltip
} from '@/components/core'
import { classNameBuilder } from '@/helpers'

export enum AnalysisType {
  GRAMMAR = 'GRAMMAR',
  VOCABULARY = 'VOCABULARY',
  COHERENCE = 'COHERENCE'
}

enum AnalysisMetricType {
  LENGTH = 'LENGTH',
  GRAMMATICAL_ACCURACY = 'GRAMMATICAL_ACCURACY',
  GRAMMATICAL_RANGE = 'GRAMMATICAL_RANGE',
  LEXICAL_RANGE = 'LEXICAL_RANGE',
  COLLOCATION_COMMONALITY = 'COLLOCATION_COMMONALITY',
  NATURAL_PHRASES = 'NATURAL_PHRASES',
  CONTENT_WORDS = 'CONTENT_WORDS',
  CONNECTORS = 'CONNECTORS',
  PRONOUN_DENSITY = 'PRONOUN_DENSITY',
  ADVERB_DIVERSITY = 'ADVERB_DIVERSITY',
  VERB_DIVERSITY = 'VERB_DIVERSITY'
}

type AnalysisCardDataType = {
  score: number
  isResponsive?: boolean
  analysisType: AnalysisType
  chartItems: { [key: string]: number }
}

type GrammarMetrics = {
  grammaticalAccuracy: number
  grammaticalRange: number
  length: number
  lexicalDiversity: number
}

type VocabularyMetrics = {
  academicLanguageUse: number
  collocationCommonality: number
  idiomaticity: number
  lexicalDiversity: number
  wordSophistication: number
  wordSpecificity: number
}

type CoherenceMetrics = {
  adverbDiversity: number
  basicConnectives: number
  causalConnectives: number
  lexicalDensity: number
  negativeConnectives: number
  pronounDensity: number
  verbDiversity: number
}

const METRICS_DETAILS = {
  GRAMMAR: {
    [AnalysisMetricType.LENGTH]: {
      titleKey: 'length_title_analysis_card',
      aboutKey: 'length_about_analysis_card',
      example: {
        badKey: 'length_bad_example_analysis_card',
        goodKey: 'length_good_example_analysis_card'
      }
    },
    [AnalysisMetricType.GRAMMATICAL_ACCURACY]: {
      titleKey: 'grammatical_accuracy_title_analysis_card',
      aboutKey: 'grammatical_accuracy_about_analysis_card',
      example: {
        badKey: 'grammatical_accuracy_bad_example_analysis_card',
        goodKey: 'grammatical_accuracy_good_example_analysis_card'
      }
    },
    [AnalysisMetricType.GRAMMATICAL_RANGE]: {
      titleKey: 'grammatical_range_title_analysis_card',
      aboutKey: 'grammatical_range_about_analysis_card',
      example: {
        badKey: 'grammatical_range_bad_example_analysis_card',
        goodKey: 'grammatical_range_good_example_analysis_card'
      }
    }
  },
  VOCABULARY: {
    [AnalysisMetricType.LEXICAL_RANGE]: {
      titleKey: 'lexical_range_title_analysis_card',
      aboutKey: 'lexical_range_about_analysis_card',
      example: {
        badKey: 'lexical_range_bad_example_analysis_card',
        goodKey: 'lexical_range_good_example_analysis_card'
      }
    },
    [AnalysisMetricType.COLLOCATION_COMMONALITY]: {
      titleKey: 'collocation_commonality_title_analysis_card',
      aboutKey: 'collocation_commonality_about_analysis_card',
      example: {
        badKey: 'collocation_commonality_bad_example_analysis_card',
        goodKey: 'collocation_commonality_good_example_analysis_card'
      }
    },
    [AnalysisMetricType.NATURAL_PHRASES]: {
      titleKey: 'natural_phrases_title_analysis_card',
      aboutKey: 'natural_phrases_about_analysis_card',
      example: {
        badKey: 'natural_phrases_bad_example_analysis_card',
        goodKey: 'natural_phrases_good_example_analysis_card'
      }
    }
  },
  COHERENCE: {
    [AnalysisMetricType.CONTENT_WORDS]: {
      titleKey: 'content_words_title_analysis_card',
      aboutKey: 'content_words_about_analysis_card',
      example: {
        badKey: 'content_words_bad_example_analysis_card',
        goodKey: 'content_words_good_example_analysis_card'
      }
    },
    [AnalysisMetricType.CONNECTORS]: {
      titleKey: 'connectors_title_analysis_card',
      aboutKey: 'connectors_about_analysis_card',
      example: {
        badKey: 'connectors_bad_example_analysis_card',
        goodKey: 'connectors_good_example_analysis_card'
      }
    },
    [AnalysisMetricType.PRONOUN_DENSITY]: {
      titleKey: 'pronoun_density_title_analysis_card',
      aboutKey: 'pronoun_density_about_analysis_card',
      example: {
        badKey: 'pronoun_density_bad_example_analysis_card',
        goodKey: 'pronoun_density_good_example_analysis_card'
      }
    },
    [AnalysisMetricType.ADVERB_DIVERSITY]: {
      titleKey: 'adverb_diversity_title_analysis_card',
      aboutKey: 'adverb_diversity_about_analysis_card',
      example: {
        badKey: 'adverb_diversity_bad_example_analysis_card',
        goodKey: 'adverb_diversity_good_example_analysis_card'
      }
    },
    [AnalysisMetricType.VERB_DIVERSITY]: {
      titleKey: 'verb_diversity_title_analysis_card',
      aboutKey: 'verb_diversity_about_analysis_card',
      example: {
        badKey: 'verb_diversity_bad_example_analysis_card',
        goodKey: 'verb_diversity_good_example_analysis_card'
      }
    }
  }
}

function ExampleTag({
  variant,
  text
}: {
  variant: 'bad' | 'good'
  text: string
}) {
  return (
    <Row
      className={classNameBuilder(
        'w-full items-center space-x-4 rounded-lg p-3',
        { bad: 'bg-electric-indigo-50', good: 'bg-[#08AD85] bg-opacity-20' }[
          variant
        ]
      )}
    >
      <Row
        className={classNameBuilder(
          'h-8 w-8 shrink-0 items-center justify-center rounded-full',
          { bad: 'bg-electric-indigo-100', good: 'bg-[#A5DCCF]' }[variant]
        )}
      >
        {
          {
            bad: (
              <Frown
                size={16}
                className="text-electric-indigo-600"
                strokeWidth={3}
              />
            ),
            good: <PartyPopper size={16} strokeWidth={3} color="#08AD85" />
          }[variant]
        }
      </Row>
      <Text.Translated size="xs" color="500" translationKey={text} />
    </Row>
  )
}

function formatData(type: AnalysisType, data: { [key: string]: number }) {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const outputData: any = {}
  if (type === AnalysisType.GRAMMAR) {
    const grammaticalData = data as GrammarMetrics
    outputData[AnalysisMetricType.LENGTH] = round(grammaticalData['length'], 1)
    outputData[AnalysisMetricType.GRAMMATICAL_ACCURACY] = round(
      grammaticalData.grammaticalAccuracy,
      1
    )
    outputData[AnalysisMetricType.GRAMMATICAL_RANGE] = round(
      (grammaticalData.lexicalDiversity + grammaticalData.grammaticalRange) / 2,
      1
    )
  }
  if (type === AnalysisType.VOCABULARY) {
    const vocabularyData = data as VocabularyMetrics
    outputData[AnalysisMetricType.LEXICAL_RANGE] = round(
      (vocabularyData.lexicalDiversity +
        vocabularyData.wordSophistication +
        vocabularyData.wordSpecificity +
        vocabularyData.academicLanguageUse) /
        4,
      1
    )
    outputData[AnalysisMetricType.COLLOCATION_COMMONALITY] = round(
      vocabularyData.collocationCommonality,
      1
    )
    outputData[AnalysisMetricType.NATURAL_PHRASES] = round(
      vocabularyData.idiomaticity,
      1
    )
  }
  if (type === AnalysisType.COHERENCE) {
    const coherenceData = data as CoherenceMetrics
    outputData[AnalysisMetricType.CONTENT_WORDS] = round(
      coherenceData.lexicalDensity,
      1
    )
    outputData[AnalysisMetricType.CONNECTORS] = round(
      (coherenceData.basicConnectives +
        coherenceData.causalConnectives +
        coherenceData.negativeConnectives) /
        3,
      1
    )
    outputData[AnalysisMetricType.PRONOUN_DENSITY] = round(
      coherenceData.pronounDensity,
      1
    )
    outputData[AnalysisMetricType.ADVERB_DIVERSITY] = round(
      coherenceData.adverbDiversity,
      1
    )
    outputData[AnalysisMetricType.VERB_DIVERSITY] = round(
      coherenceData.verbDiversity,
      1
    )
  }

  return outputData as { [key: string]: number }
}

const AnalysisCard = ({
  score,
  analysisType,
  chartItems,
  isResponsive = true
}: AnalysisCardDataType) => {
  if (!analysisType || !chartItems) return
  const formattedData = formatData(analysisType, chartItems)

  return (
    <Flex
      className={classNameBuilder(
        'h-full w-full justify-between bg-white',
        isResponsive &&
          'flex-col max-lg:space-y-6 max-lg:py-8 lg:flex-row lg:rounded-2xl lg:border lg:border-gray-50 lg:p-14',
        !isResponsive && 'flex-row rounded-2xl border border-gray-50 p-8'
      )}
    >
      <Flex
        className={classNameBuilder(
          'h-full w-1/3 flex-wrap items-center gap-2',
          isResponsive && 'flex-row max-lg:w-full max-lg:justify-center',
          !isResponsive && 'flex-col'
        )}
      >
        <Row className="w-full items-center justify-between max-lg:w-full max-lg:items-center">
          <Text.Translated
            translationKey={
              {
                [AnalysisType.GRAMMAR]: 'grammar_analysis_type',
                [AnalysisType.VOCABULARY]: 'vocabulary_analysis_type',
                [AnalysisType.COHERENCE]: 'coherence_analysis_type'
              }[analysisType]
            }
            size="md"
            fontWeight="bold"
            className="text-[#353535] max-lg:text-base"
          />
          <Text size="sm" className="!text-[#6A0DFD]" fontWeight="bold">
            {round(score / 10, 1).toPrecision(2)} / 10
          </Text>
        </Row>
        <Column className="h-3 w-full">
          <ProgressBar
            className={{
              wrapper: 'h-3 w-full bg-[#E0E0E0]',
              divider: false,
              bar: 'rounded-lg bg-gradient-to-r from-[#09D4E1] to-[#6A0DFD]'
            }}
            progress={score / 100}
          />
        </Column>
      </Flex>
      <Row
        className={classNameBuilder(
          'h-full flex-wrap items-center gap-4',
          isResponsive && 'w-2/5 max-lg:w-full max-lg:self-center',
          !isResponsive && 'w-3/5'
        )}
      >
        <Row className="w-full">
          <Text.Translated
            size="xs"
            fontWeight="bold"
            className={classNameBuilder(
              'text-[#353535]',
              isResponsive && 'max-lg:text-sm',
              !isResponsive && 'text-sm'
            )}
            translationKey="score_detail_analysis_card"
          />
        </Row>
        <Column className="w-full space-y-4">
          {Object.entries(formattedData).map(([itemName, itemValue], index) => {
            const metricTypeLabels = get(
              METRICS_DETAILS[analysisType],
              itemName,
              null
            ) as {
              titleKey: string
              aboutKey: string
              example: {
                badKey: string
                goodKey: string
              }
            } | null

            return (
              <Flex
                key={index}
                className={classNameBuilder(
                  'h-full w-full flex-row flex-wrap items-center gap-2',
                  isResponsive && 'max-lg:justify-center lg:flex-row',
                  !isResponsive && 'flex-row'
                )}
              >
                <Row
                  className={classNameBuilder(
                    'w-full items-center justify-between',
                    isResponsive && 'max-lg:w-full max-lg:items-center'
                  )}
                >
                  <div className="flex items-center space-x-1">
                    <Text.Translated
                      translationKey={`${itemName.toLowerCase()}_analysis_card`}
                      size="xs"
                      fontWeight="regular"
                      className={classNameBuilder(
                        isResponsive && 'max-lg:text-sm',
                        !isResponsive && 'text-sm'
                      )}
                      color="600"
                    />
                    {metricTypeLabels ? (
                      <Tooltip
                        content={
                          <Column
                            className={classNameBuilder(
                              'w-[100%] max-w-[400px] space-y-4',
                              isResponsive && 'max-md:w-[250px]'
                            )}
                          >
                            <Text.Translated
                              size="sm"
                              className="!text-electric-indigo-500"
                              fontWeight="semibold"
                              translationKey={metricTypeLabels.titleKey}
                            />
                            <Text.Translated
                              size="xs"
                              color="800"
                              translationKey={metricTypeLabels.aboutKey}
                            />
                            <Text.Translated
                              size="xs"
                              color="800"
                              translationKey="example_label_analysis_card"
                            />
                            <Column className="items-center space-y-2">
                              <ExampleTag
                                variant="bad"
                                text={metricTypeLabels.example.badKey}
                              />
                              <ArrowDown size={24} color="#818181" />
                              <ExampleTag
                                variant="good"
                                text={metricTypeLabels.example.goodKey}
                              />
                            </Column>
                          </Column>
                        }
                        style="light"
                        placement="top"
                        animation="duration-500"
                        theme={{
                          arrow: {
                            style: { light: 'shadow-md' }
                          }
                        }}
                      >
                        <Info
                          size={16}
                          className="text-[#C0C0C0] hover:text-[#9B5EFD]"
                        />
                      </Tooltip>
                    ) : null}
                  </div>
                  <Text
                    size="sm"
                    className="!text-[#6A0DFD] max-lg:text-sm"
                    fontWeight="bold"
                  >
                    {itemValue.toPrecision(2)} / 10
                  </Text>
                </Row>
                <Column className="h-3 w-full">
                  <ProgressBar
                    className={{
                      wrapper: 'h-3 w-full bg-[#E0E0E0]',
                      divider: false,
                      bar: 'rounded-lg bg-gradient-to-r from-[#09D4E1] to-[#6A0DFD]'
                    }}
                    progress={itemValue / 10}
                  />
                </Column>
              </Flex>
            )
          })}
        </Column>
      </Row>
    </Flex>
  )
}

export default AnalysisCard
